// [2026-08-07] ADDED — shared presentational pieces and formatters.
//
// These live in one file because they are the vocabulary every screen is written in: if a tile, a
// badge or a duration renders differently on two pages, the dashboard looks broken even when the
// data is right.
//
// THE FORMATTERS ARE THE IMPORTANT PART. Every one of them distinguishes "no value" from "zero".
// A dashboard that prints 0 ms for a client with no traffic, or 0 credits for one whose balance is
// simply unknown, is stating something false — and these tiles are what a customer uses to decide
// whether they are being billed correctly.

import { useEffect, useState } from 'react'
// [2026-08-11] ADDED — Card and Tile below are now shadcn underneath. Aliased because this module
// exports its own `Card` with a different (title/subtitle/actions) API that every screen already
// calls; renaming that would be a twelve-file change for no benefit.
import {
  Card as ShadCard,
  CardContent as ShadCardContent,
  CardDescription as ShadCardDescription,
  CardHeader as ShadCardHeader,
  CardTitle as ShadCardTitle,
} from './components/ui/card.jsx'
import { cn } from './lib/utils.js'

// ── formatters ────────────────────────────────────────────────────────────────

export function num(value, { decimals = 0 } = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  })
}

// [2026-09-17] ADDED — credits, written the way a person writes them.
//
// Asked for by Rujal on 2026-09-17: "remove the .00, only the credits should be there, no decimal".
// Every credit figure on the dashboard went through num(x, {decimals: 2}), so a whole balance read
// "967.00" and a movement read "+1.00".
//
// NOT `Math.round`, and not `decimals: 0`. Either would turn a genuine half credit into "1" or "0"
// — and a billing screen that rounds a real fraction away is worse than one with a trailing zero
// on it. minimumFractionDigits 0 with a maximum of 3 drops the noise and keeps the truth: 967,
// +1, 33 — but 0.5 stays 0.5. Three, because the ledger stores milli-credits and
// credits.py:to_credits already rounds to three places, so that is the finest real figure.
export function credits(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: 3,
  })
}

// [2026-09-17] ADDED — ledger reasons in English.
//
// The Credits & Billing ledger printed the raw machine value: `released_interview_abandoned`. That
// is the string the settler writes (app/core/interview_billing.py REASON_*), and it is the right
// thing to STORE — alerts and support key on it and it must never be reworded server-side. It is
// the wrong thing to show a customer, who has to guess what "abandoned" means and why their credit
// moved.
//
// THE KEYS ARE THE SERVER'S OWN CONSTANTS, all eight of them, read from
// origin/test:app/core/interview_billing.py on 2026-09-17.
//
// ANYTHING UNRECOGNISED IS RETURNED UNCHANGED, which matters more than the mapping: most ledger
// reasons are already prose ("Estimated cost of video analysis", "Reservation returned"), and an
// operator's grant reason is free text they typed. Mapping must never swallow a reason it does not
// know — a customer seeing a slightly technical string beats one seeing nothing.
const LEDGER_REASON = {
  interview_billing_off: 'Per-interview billing was off',
  charged_at_mint: 'Charged when the session started',
  held_pending_analysis: 'Held until the analysis finishes',
  settled_analysis_complete: 'Charged — analysis finished',
  released_analysis_failed: 'Returned — the analysis failed',
  released_hold_expired: 'Returned — the hold expired',
  released_interview_abandoned: 'Returned — no recording was submitted',
  released_analysis_stalled: 'Returned — the analysis never finished',
}

export function reasonText(reason) {
  if (!reason) return ''
  return LEDGER_REASON[reason] || String(reason)
}

export function ms(value) {
  if (value === null || value === undefined) return '—'
  if (value >= 1000) return `${(value / 1000).toFixed(2)}s`
  return `${Math.round(value)}ms`
}

export function pct(value, { decimals = 1 } = {}) {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(decimals)}%`
}

/** Offset into a recording, the way a video player shows it: 9:40, or 1:09:40 past an hour.
 *  Mirrors app/core/timeline.py:clock so the API and the UI never disagree about a timestamp. */
export function clock(seconds) {
  if (seconds === null || seconds === undefined) return '—'
  const total = Math.max(Math.round(Number(seconds)), 0)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

export function when(iso) {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return String(iso)
  return date.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function shortDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ── primitives ────────────────────────────────────────────────────────────────

// [2026-08-11] CHANGED — Card and Tile are now built on the shadcn primitives in
// components/ui/card.jsx, so every screen that already uses them converts at once.
//
// WHY REBUILD THE PRIMITIVE INSTEAD OF EDITING EIGHT SCREENS. Overview, Analytics, Credits,
// Recordings, Keys, Upload, Candidates and the four Admin screens all render <Card> and <Tile>
// from here. Rewriting each one would be twelve files of churn, twelve chances to break a working
// screen, and twenty-five e2e tests to re-verify for a change that is purely presentational.
// Changing the primitive gets the same result in one place.
//
// THE `card` AND `tile` CLASS NAMES ARE KEPT DELIBERATELY. Six e2e locators target `.tile`, and
// keeping the hook means the migration is invisible to every test that was asserting on
// behaviour rather than on styling. Their VISUAL rules in theme.css are commented out in the same
// change — otherwise two systems would style the same element and which one won would depend on
// stylesheet order, which is exactly the kind of bug that appears only after a rebuild.
//
// --- OLD (pre-2026-08-11) ---
// export function Card({ title, subtitle, actions, children, className = '' }) {
//   return (
//     <section className={`card ${className}`}>
//       {(title || actions) && (
//         <div className="card-head">
//           <div>
//             {title && <h3>{title}</h3>}
//             {subtitle && <p>{subtitle}</p>}
//           </div>
//           {actions}
//         </div>
//       )}
//       {children}
//     </section>
//   )
// }
// export function Tile({ label, value, unit, sub, tone }) {
//   return (
//     <div className="tile">
//       <div className="tile-label">{label}</div>
//       <div className="tile-value" style={tone ? { color: tone } : undefined}>
//         {value}{unit && <span className="unit"> {unit}</span>}
//       </div>
//       {sub && <div className="tile-sub">{sub}</div>}
//     </div>
//   )
// }
// --- END OLD ---
export function Card({ title, subtitle, actions, children, className = '' }) {
  return (
    <ShadCard className={cn('card', className)}>
      {(title || actions) && (
        <ShadCardHeader className="flex-row items-baseline justify-between gap-3 pb-3">
          <div>
            {title && <ShadCardTitle>{title}</ShadCardTitle>}
            {subtitle && <ShadCardDescription>{subtitle}</ShadCardDescription>}
          </div>
          {actions}
        </ShadCardHeader>
      )}
      {/* pt-0 when there is a header: CardHeader already supplies the top spacing, and doubling
          it leaves a gap that looks like a rendering fault rather than a design choice. */}
      <ShadCardContent className={title || actions ? 'pt-0' : 'pt-5'}>{children}</ShadCardContent>
    </ShadCard>
  )
}

export function Tile({ label, value, unit, sub, tone }) {
  return (
    <ShadCard className="tile">
      <ShadCardContent className="pt-4">
        <div className="text-[10.5px] font-semibold uppercase tracking-[.07em] text-muted-foreground">
          {label}
        </div>
        <div className="mt-[7px] text-[26px] font-semibold leading-[1.15] tracking-[-.02em]"
             style={tone ? { color: tone } : undefined}>
          {value}
          {unit && <span className="text-[14px] font-medium text-muted-foreground"> {unit}</span>}
        </div>
        {sub && <div className="mt-[5px] text-[12px] text-muted-foreground">{sub}</div>}
      </ShadCardContent>
    </ShadCard>
  )
}

/** Severity always renders colour AND text. Never the dot alone — a colour-blind reviewer
 *  deciding whether to escalate a real candidate cannot be asked to read hue. */
export function Severity({ level }) {
  const known = ['high', 'medium', 'low'].includes(level)
  return <span className={`badge ${known ? level : 'low'}`}><span className="dot" />{level || 'none'}</span>
}

export function StatusBadge({ status }) {
  const tone = { completed: 'good', failed: 'high', processing: 'medium',
                 queued: 'low', pending: 'low' }[status] || 'low'
  return <span className={`badge ${tone}`}><span className="dot" />{status || 'unknown'}</span>
}

export function Empty({ title, hint }) {
  return <div className="empty"><strong>{title}</strong>{hint && <span>{hint}</span>}</div>
}

export function Skeleton({ height = 80, style }) {
  return <div className="skeleton" style={{ height, ...style }} />
}

export function Notice({ kind = 'info', children }) {
  if (!children) return null
  return <div className={`notice ${kind}`}>{children}</div>
}

/** Time-window picker. One control, above the charts, applying to every panel on the screen —
 *  a per-chart range control makes two panels silently show different windows. */
export function RangePicker({ value, onChange, options = [7, 30, 90] }) {
  return (
    <div className="seg" role="group" aria-label="Time range">
      {options.map((days) => (
        <button key={days} type="button"
                className={value === days ? 'active' : ''}
                onClick={() => onChange(days)}>
          {days}d
        </button>
      ))}
    </div>
  )
}

/** A magnitude bar behind a table figure. The NUMBER is always rendered — the bar is a secondary
 *  encoding, never the only way to read the value. */
export function BarCell({ value, max, format = num }) {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0
  return (
    <td className="bar-cell num">
      <span className="bar" style={{ width: `${width}%` }} />
      <span className="bar-label">{format(value)}</span>
    </td>
  )
}

export function Modal({ title, subtitle, onClose, children, wide }) {
  // Escape closes. A modal that can only be dismissed by hitting a small × is a papercut on every
  // single use, and this one opens dozens of times in a review session.
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal-backdrop" onClick={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

// ── toasts ────────────────────────────────────────────────────────────────────

let pushToast = () => {}
export const toast = (message, isError = false) => pushToast(message, isError)

export function ToastHost() {
  const [items, setItems] = useState([])
  useEffect(() => {
    pushToast = (message, isError) => {
      const id = Math.random().toString(36).slice(2)
      setItems((current) => [...current, { id, message, isError }])
      setTimeout(() => setItems((current) => current.filter((t) => t.id !== id)), 4500)
    }
    return () => { pushToast = () => {} }
  }, [])
  return (
    <div className="toast-root">
      {items.map((item) => (
        <div key={item.id} className={`toast ${item.isError ? 'error' : ''}`}>{item.message}</div>
      ))}
    </div>
  )
}

// ── data loading ──────────────────────────────────────────────────────────────

/** Load once, expose {data, error, loading, reload}.
 *
 *  `deps` drives the refetch. The abort on unmount matters here: switching screens quickly with a
 *  slow MongoDB behind the API would otherwise let a stale response land after the new one and
 *  repaint the page with the previous screen's numbers.
 */
export function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let live = true
    setState((current) => ({ ...current, loading: true, error: null }))
    loader()
      .then((data) => { if (live) setState({ data, error: null, loading: false }) })
      .catch((err) => {
        if (!live || err.name === 'AbortError') return
        setState({ data: null, error: err, loading: false })
      })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { ...state, reload: () => setNonce((n) => n + 1) }
}

/** Download an array of objects as CSV. Client-side so it costs the API nothing.
 *
 *  Values are quoted and inner quotes doubled — a client name containing a comma would otherwise
 *  shift every following column, and the export would be silently wrong rather than obviously so. */
export function downloadCsv(filename, rows) {
  if (!rows || !rows.length) { toast('Nothing to export.', true); return }
  const columns = Object.keys(rows[0])
  const escape = (value) => {
    if (value === null || value === undefined) return ''
    let text = typeof value === 'object' ? JSON.stringify(value) : String(value)

    // [2026-08-11] ADDED — neutralise spreadsheet formula injection.
    //
    // FOUND BY THE 2026-08-11 SECURITY AUDIT. Quoting protects the CSV STRUCTURE; it does nothing
    // about what a spreadsheet does with the contents. Excel, LibreOffice and Google Sheets all
    // treat a cell beginning = + - @ (or a leading tab/CR) as a FORMULA, and quotes do not stop
    // that. Several columns exported here are attacker-controlled: `reference_id` is chosen by
    // the customer's own integration, and candidate/session ids flow through it too.
    //
    // So a client could set reference_id to `=cmd|'/c calc'!A1` (or a WEBSERVICE()/HYPERLINK() call
    // that exfiltrates the rest of the row), and the payload would execute on the workstation of
    // whoever exported the CSV — an InCruiter operator, on a file they have every reason to trust
    // because it came from their own dashboard.
    //
    // A leading apostrophe is the standard neutraliser: spreadsheets treat the cell as text and do
    // not display the apostrophe. The value is unchanged for anything that parses the CSV properly.
    // Fixed HERE rather than at the seven call sites, so no future export can forget it.
    if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`

    return `"${text.replace(/"/g, '""')}"`
  }
  const csv = [
    columns.map(escape).join(','),
    ...rows.map((row) => columns.map((column) => escape(row[column])).join(',')),
  ].join('\n')

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
